import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';

const COLORS = {
  SECURITY_TRAILS: '#0969da',
  PUBLICWWW: '#cf222e',
};

const DEFAULT_VISIBLE = {
  SECURITY_TRAILS: true,
  PUBLICWWW: true,
};

function parseDay(iso) {
  return d3.timeParse('%Y-%m-%d')(iso);
}

function chartFonts() {
  if (typeof document === 'undefined') {
    return { ui: "'Source Sans 3', sans-serif", data: "'IBM Plex Mono', monospace" };
  }
  const cs = getComputedStyle(document.documentElement);
  return {
    ui: cs.getPropertyValue('--argus-chart-font-ui').trim() || "'Source Sans 3', sans-serif",
    data: cs.getPropertyValue('--argus-chart-font-data').trim() || "'IBM Plex Mono', monospace",
  };
}

export default function ApiLimitsChart({ apis, viewMode }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [visible, setVisible] = useState(() => ({ ...DEFAULT_VISIBLE }));
  const [dims, setDims] = useState({ width: 800, height: 360 });

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth || 800;
    setDims({ width: Math.max(320, w), height: 380 });
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    if (!apis || !apis.length || !svgRef.current) return;

    const { ui: fontUi, data: fontData } = chartFonts();

    const margin = { top: 28, right: 28, bottom: 48, left: 56 };
    const width = dims.width;
    const height = dims.height;
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const first = apis.find((a) => a.dailyUsage && a.dailyUsage.length);
    if (!first) {
      g.append('text')
        .attr('x', innerW / 2)
        .attr('y', innerH / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#666')
        .attr('font-family', fontUi)
        .attr('font-size', 12)
        .text('No usage data in this range');
      return;
    }

    const days = first.dailyUsage.map((d) => parseDay(d.date)).filter(Boolean);
    const x = d3
      .scaleTime()
      .domain(d3.extent(days))
      .range([0, innerW]);

    const activeApis = apis.filter((a) => visible[a.provider] !== false);
    let maxY = 1;
    activeApis.forEach((a) => {
      a.dailyUsage.forEach((pt) => {
        maxY = Math.max(maxY, pt.count);
      });
    });
    if (viewMode === 'dailyLimit') {
      apis.forEach((a) => {
        if (a.dailyLimit && visible[a.provider] !== false) {
          maxY = Math.max(maxY, a.dailyLimit);
        }
      });
    }

    const y = d3.scaleLinear().domain([0, maxY * 1.05]).nice().range([innerH, 0]);

    const gridY = g
      .append('g')
      .attr('class', 'api-limits-grid-y')
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickSize(-innerW)
          .tickFormat(d3.format('~s'))
      )
      .call((ga) => ga.select('.domain').remove());
    gridY.selectAll('line').attr('stroke', '#e8e8e8');
    gridY.selectAll('.tick text').attr('font-family', fontData).attr('font-size', 11).attr('font-weight', 400);

    const axisX = g
      .append('g')
      .attr('class', 'api-limits-axis-x')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(Math.min(12, days.length)).tickFormat(d3.timeFormat('%b %d')));
    axisX
      .selectAll('text')
      .attr('transform', 'rotate(-35)')
      .style('text-anchor', 'end')
      .attr('font-family', fontUi)
      .attr('font-size', 11)
      .attr('font-weight', 400);

    g.append('text')
      .attr('x', -innerH / 2)
      .attr('y', -42)
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'middle')
      .attr('fill', '#444')
      .attr('font-family', fontUi)
      .attr('font-size', 12)
      .attr('font-weight', 600)
      .text('Requests (count)');

    if (viewMode === 'dailyLimit') {
      apis.forEach((a) => {
        if (!a.dailyLimit || visible[a.provider] === false) return;
        const cy = y(a.dailyLimit);
        g.append('line')
          .attr('x1', 0)
          .attr('x2', innerW)
          .attr('y1', cy)
          .attr('y2', cy)
          .attr('stroke', COLORS[a.provider] || '#999')
          .attr('stroke-dasharray', '6 4')
          .attr('opacity', 0.7);
        const capEl = g
          .append('text')
          .attr('x', innerW - 4)
          .attr('y', cy - 4)
          .attr('text-anchor', 'end')
          .attr('fill', COLORS[a.provider] || '#999');
        capEl
          .append('tspan')
          .attr('font-family', fontUi)
          .attr('font-size', 11)
          .text(`${a.name} daily cap (`);
        capEl.append('tspan').attr('font-family', fontData).attr('font-size', 11).text(String(a.dailyLimit));
        capEl.append('tspan').attr('font-family', fontUi).attr('font-size', 11).text(')');
      });
    }

    const line = d3
      .line()
      .x((d) => x(parseDay(d.date)))
      .y((d) => y(d.count))
      .curve(d3.curveMonotoneX);

    const focus = g.append('g').style('display', 'none');
    focus.append('line').attr('class', 'api-limits-cross-x').attr('stroke', '#999').attr('stroke-dasharray', '4 2');

    activeApis.forEach((a) => {
      g.append('path')
        .datum(a.dailyUsage)
        .attr('fill', 'none')
        .attr('stroke', COLORS[a.provider] || '#333')
        .attr('stroke-width', 2)
        .attr('d', line);

      g.selectAll(`.dot-${a.provider}`)
        .data(a.dailyUsage)
        .enter()
        .append('circle')
        .attr('class', `dot-${a.provider}`)
        .attr('cx', (d) => x(parseDay(d.date)))
        .attr('cy', (d) => y(d.count))
        .attr('r', 3)
        .attr('fill', COLORS[a.provider] || '#333');
    });

    const overlay = g
      .append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    const tip = d3
      .select(containerRef.current)
      .select('.api-limits-tooltip-floating')
      .style('opacity', 0);

    overlay.on('mousemove', function (event) {
      const [mx] = d3.pointer(event, this);
      const date = x.invert(mx);
      let dayRow = first.dailyUsage[0];
      let minDiff = Infinity;
      first.dailyUsage.forEach((d) => {
        const t = parseDay(d.date);
        const diff = Math.abs(t - date);
        if (diff < minDiff) {
          minDiff = diff;
          dayRow = d;
        }
      });
      const px = x(parseDay(dayRow.date));
      const dayIdx = first.dailyUsage.indexOf(dayRow);

      focus.style('display', null);
      focus.select('.api-limits-cross-x').attr('x1', px).attr('x2', px).attr('y1', 0).attr('y2', innerH);

      const lines = apis
        .filter((a) => visible[a.provider] !== false)
        .map((a) => {
          const pt = a.dailyUsage[dayIdx];
          if (!pt) {
            return `<div class="api-limits-tip-row"><span>${a.name}: —</span></div>`;
          }
          const warn =
            a.dailyLimit && pt.dailyPercentage != null && pt.dailyPercentage >= 80 ? ' (!)' : '';
          return `<div class="api-limits-tip-row"><span>${a.name}:</span> <span class="api-limits-tip-mono">${pt.count}</span> <span class="api-limits-tip-rest">(MTD ${pt.monthlyPercentage ?? '—'}%, day ${pt.dailyPercentage ?? '—'}%)${warn}</span></div>`;
        })
        .join('');

      const box = containerRef.current.getBoundingClientRect();
      tip
        .style('opacity', 1)
        .html(`<div class="api-limits-tip-date">${dayRow.date}</div>${lines}`)
        .style('left', `${event.clientX - box.left + 12}px`)
        .style('top', `${event.clientY - box.top + 8}px`);
    });

    overlay.on('mouseleave', () => {
      focus.style('display', 'none');
      tip.style('opacity', 0);
    });
  }, [apis, dims, visible, viewMode]);

  const toggle = (provider) => {
    setVisible((v) => ({ ...v, [provider]: !v[provider] }));
  };

  if (!apis || !apis.length) {
    return <div className="api-limits-chart-empty">No API series to display.</div>;
  }

  return (
    <div className="api-limits-chart-wrap" ref={containerRef}>
      <div className="api-limits-legend">
        {apis.map((a) => (
          <button
            key={a.provider}
            type="button"
            className={`api-limits-legend-item ${visible[a.provider] !== false ? 'on' : 'off'}`}
            onClick={() => toggle(a.provider)}
            style={{ borderColor: COLORS[a.provider] }}
          >
            <span className="api-limits-swatch" style={{ background: COLORS[a.provider] }} />
            {a.name}
          </button>
        ))}
      </div>
      <svg ref={svgRef} className="api-limits-svg" />
      <div className="api-limits-tooltip-floating" />
    </div>
  );
}
